/**
 * Reuse contract: inline integer quantity editor (idle display + edit mode with ±1/±5/±10,
 * free digit-only input, confirm-on-zero / confirm-on-|Δ|≥largeDelta). Inventory list is the
 * first consumer; other admin list screens may reuse by passing labels + onCommit.
 */
import { memo, useCallback, useEffect, useId, useRef, useState, type MouseEvent } from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '../surfacePrimitives.js';
import { confirmApi } from '../confirm/confirmApi.js';
import {
  formatQuantityDraftText,
  parseQuantityDraftText,
  QUANTITY_STEP_SIZES,
  sanitizeQuantityDraftText,
  type QuantityStepSize,
} from './quantityDraft.js';

/** Confirm when |next − current| meets or exceeds this delta (plan lock N=50). */
const DEFAULT_LARGE_QTY_DELTA = 50;

const IDLE_ROW_CLASS = 'inline-flex items-center gap-[var(--spacing-xs)]';
const QTY_VALUE_CLASS =
  'text-[length:var(--font-size-sm)] font-[number:var(--font-weight-semibold)] tabular-nums leading-none text-[var(--color-an-text)]';
const QTY_VALUE_ZERO_CLASS = `${QTY_VALUE_CLASS} text-[var(--color-an-danger)]`;
const QTY_UNIT_CLASS =
  'text-[length:var(--font-size-xs)] font-[number:var(--font-weight-medium)] lowercase text-[var(--color-an-text-muted)]';
const EDIT_ROW_CLASS =
  'inline-flex max-w-full min-w-0 flex-wrap items-center gap-[var(--spacing-xs)]';
const STEP_GROUP_CLASS = 'inline-flex min-w-0 flex-wrap items-center gap-[var(--spacing-xs)]';
const INPUT_CLASS =
  'form-input h-8 w-16 min-w-0 shrink-0 !rounded-[var(--radius-md)] !px-1.5 !py-1 text-center text-[length:var(--font-size-sm)] font-[number:var(--font-weight-semibold)] tabular-nums';
const ICON_BTN_CLASS = '!h-8 !w-8 !min-w-8 !shrink-0 !gap-0 !px-0';
const STEP_BTN_CLASS =
  '!h-8 !min-w-[1.75rem] !shrink-0 !px-1 text-[length:var(--font-size-xs)] leading-none';

export interface InlineQuantityEditorLabels {
  readonly quantityUnit: string;
  readonly editQuantityTitle: string;
  readonly decreaseQtyBy: (step: number) => string;
  readonly increaseQtyBy: (step: number) => string;
  readonly setQuantity: string;
  readonly changeQuantity: string;
  readonly savingQty: string;
  readonly saveTitle: string;
  readonly cancelTitle: string;
  readonly confirmZeroTitle: string;
  readonly confirmZeroMessage: (quantity: number) => string;
  readonly confirmLargeChangeTitle: string;
  readonly confirmLargeChangeMessage: (args: {
    readonly from: number;
    readonly to: number;
    readonly delta: number;
  }) => string;
  /** Polite live-region announcement after a successful save. */
  readonly saveAnnouncement: string;
}

export interface InlineQuantityEditorProps {
  readonly value: number;
  readonly canManage: boolean;
  readonly isLoading: boolean;
  readonly onCommit: (quantity: number) => Promise<void>;
  readonly onEditingChange?: (isEditing: boolean) => void;
  readonly labels: InlineQuantityEditorLabels;
  /** Override confirm threshold; defaults to 50. */
  readonly largeQtyDelta?: number;
}

export const InlineQuantityEditor = memo((props: InlineQuantityEditorProps): JSX.Element => {
  const {
    value,
    canManage,
    isLoading,
    onCommit,
    onEditingChange,
    labels,
    largeQtyDelta = DEFAULT_LARGE_QTY_DELTA,
  } = props;
  const inputId = useId();
  const groupId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState(formatQuantityDraftText(value));
  const [liveMessage, setLiveMessage] = useState('');
  const restoreFocusAfterSaveRef = useRef(false);

  const draftQuantity = parseQuantityDraftText(draftText);

  useEffect(() => {
    if (!isEditing) {
      setDraftText(formatQuantityDraftText(value));
    }
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
      return;
    }
    if (restoreFocusAfterSaveRef.current) {
      restoreFocusAfterSaveRef.current = false;
      editButtonRef.current?.focus();
    }
  }, [isEditing]);

  const setEditing = useCallback(
    (next: boolean): void => {
      setIsEditing(next);
      onEditingChange?.(next);
    },
    [onEditingChange]
  );

  const startEditing = useCallback(
    (event?: MouseEvent<HTMLButtonElement>): void => {
      event?.stopPropagation();
      if (!canManage || isLoading) {
        return;
      }
      setDraftText(formatQuantityDraftText(value));
      setEditing(true);
    },
    [canManage, isLoading, setEditing, value],
  );

  const cancelEditing = useCallback(
    (event?: MouseEvent<HTMLButtonElement>): void => {
      event?.stopPropagation();
      setDraftText(formatQuantityDraftText(value));
      setEditing(false);
    },
    [setEditing, value],
  );

  const save = useCallback(async (): Promise<void> => {
    if (!canManage || isLoading) {
      return;
    }
    const next = draftQuantity;
    if (next === value) {
      setEditing(false);
      return;
    }

    const delta = Math.abs(next - value);
    if (next === 0 || delta >= largeQtyDelta) {
      const confirmed = await confirmApi({
        title: next === 0 ? labels.confirmZeroTitle : labels.confirmLargeChangeTitle,
        message:
          next === 0
            ? labels.confirmZeroMessage(value)
            : labels.confirmLargeChangeMessage({ from: value, to: next, delta }),
        variant: 'warning',
        confirmLabel: labels.saveTitle,
        cancelLabel: labels.cancelTitle,
      });
      if (!confirmed) {
        return;
      }
    }

    try {
      await onCommit(next);
    } catch {
      // Soft-fail / hard-fail: keep edit mode open; callers toast errors.
      return;
    }
    setLiveMessage(labels.saveAnnouncement);
    restoreFocusAfterSaveRef.current = true;
    setEditing(false);
  }, [
    canManage,
    draftQuantity,
    isLoading,
    labels,
    largeQtyDelta,
    onCommit,
    setEditing,
    value,
  ]);

  const adjustDraft = useCallback((delta: number): void => {
    setDraftText((prev) => formatQuantityDraftText(parseQuantityDraftText(prev) + delta));
  }, []);

  const handleInputChange = useCallback((raw: string): void => {
    setDraftText(sanitizeQuantityDraftText(raw));
  }, []);

  const renderStepButtons = (direction: -1 | 1): JSX.Element[] =>
    QUANTITY_STEP_SIZES.map((step: QuantityStepSize) => {
      const signedStep = step * direction;
      const label =
        direction < 0 ? labels.decreaseQtyBy(step) : labels.increaseQtyBy(step);
      const disableMinus = direction < 0 && draftQuantity === 0;
      return (
        <Button
          key={`${direction < 0 ? 'dec' : 'inc'}-${String(step)}`}
          type="button"
          intent="ghost"
          size="sm"
          className={STEP_BTN_CLASS}
          onClick={(event) => {
            event.stopPropagation();
            adjustDraft(signedStep);
          }}
          disabled={isLoading || disableMinus}
          aria-label={label}
          title={label}
        >
          {direction < 0 ? `−${String(step)}` : `+${String(step)}`}
        </Button>
      );
    });

  const liveRegion = (
    <span className="sr-only" aria-live="polite">
      {liveMessage}
    </span>
  );

  if (!canManage) {
    return (
      <div className={IDLE_ROW_CLASS} aria-busy={isLoading || undefined}>
        {liveRegion}
        <span
          className={value === 0 ? QTY_VALUE_ZERO_CLASS : QTY_VALUE_CLASS}
          data-quantity-state={value === 0 ? 'zero' : 'readonly'}
        >
          {value}
        </span>
        <span className={QTY_UNIT_CLASS}>{labels.quantityUnit}</span>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div
        id={groupId}
        className={EDIT_ROW_CLASS}
        role="group"
        aria-label={labels.editQuantityTitle}
        aria-busy={isLoading || undefined}
      >
        {liveRegion}
        <div className={STEP_GROUP_CLASS}>{renderStepButtons(-1)}</div>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          pattern="[0-9]*"
          className={INPUT_CLASS}
          value={draftText}
          disabled={isLoading}
          aria-busy={isLoading || undefined}
          aria-labelledby={groupId}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void save();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              cancelEditing();
            }
          }}
        />
        <div className={STEP_GROUP_CLASS}>{renderStepButtons(1)}</div>
        <Button
          type="button"
          intent="primary"
          size="sm"
          disabled={isLoading}
          aria-busy={isLoading || undefined}
          onClick={(event) => {
            event.stopPropagation();
            void save();
          }}
        >
          {isLoading ? labels.savingQty : labels.saveTitle}
        </Button>
        <Button
          type="button"
          intent="secondary"
          size="sm"
          disabled={isLoading}
          onClick={cancelEditing}
        >
          {labels.cancelTitle}
        </Button>
      </div>
    );
  }

  return (
    <div className={IDLE_ROW_CLASS} aria-busy={isLoading || undefined}>
      {liveRegion}
      <span
        className={value === 0 ? QTY_VALUE_ZERO_CLASS : QTY_VALUE_CLASS}
        data-quantity-state={value === 0 ? 'zero' : 'in-stock'}
      >
        {value}
      </span>
      <span className={QTY_UNIT_CLASS}>{labels.quantityUnit}</span>
      <Button
        ref={editButtonRef}
        type="button"
        intent="ghost"
        size="sm"
        className={ICON_BTN_CLASS}
        disabled={isLoading}
        onClick={(event) => startEditing(event)}
        aria-label={value === 0 ? labels.setQuantity : labels.changeQuantity}
        title={value === 0 ? labels.setQuantity : labels.changeQuantity}
      >
        <Pencil className="h-4 w-4" aria-hidden />
      </Button>
    </div>
  );
});

InlineQuantityEditor.displayName = 'InlineQuantityEditor';
